import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { MenuPackageCategory, MenuPackageItem, MenuItem } from '@planner/types'
import { useMenuItemCategories } from './useMenuItemCategories'
import { logMenuChange } from '@/lib/audit-log'
import { useAuth } from '@/contexts/AuthContext'
import { pluralizeDish } from '@/lib/utils'

interface SelectedItem {
  menu_item_id: string
  quantity_percentage: number
}

interface CategorySelection {
  categoryId: string
  selectedItems: SelectedItem[]
}

export function usePackageConfigurator(packageId: string, eventId: string, numberOfGuests: number) {
  const { getItemsForCategory } = useMenuItemCategories()
  const { user } = useAuth()
  const [packageCategories, setPackageCategories] = useState<MenuPackageCategory[]>([])
  const [packageItems, setPackageItems] = useState<MenuPackageItem[]>([])
  const [allMenuItems, setAllMenuItems] = useState<MenuItem[]>([])
  const [categoryItemsMap, setCategoryItemsMap] = useState<Record<string, MenuItem[]>>({})
  const [selections, setSelections] = useState<Record<string, CategorySelection>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchPackageData = useCallback(async () => {
    if (!packageId) return

    setLoading(true)
    setError(null)

    try {
      // Fetch package categories with category details
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('menu_package_categories')
        .select(`
          *,
          menu_category:menu_categories (*)
        `)
        .eq('menu_package_id', packageId)
        .order('display_order', { ascending: true })

      if (categoriesError) throw categoriesError

      // Fetch package items with menu item details
      const packageCategoryIds = (categoriesData || []).map((cat: MenuPackageCategory & { menu_category?: unknown }) => cat.id)
      const { data: itemsData, error: itemsError } = await supabase
        .from('menu_package_items')
        .select(`
          *,
          menu_item:menu_items (*)
        `)
        .in('menu_package_category_id', packageCategoryIds)
        .order('display_order', { ascending: true })

      if (itemsError) throw itemsError

      setPackageCategories((categoriesData || []) as MenuPackageCategory[])
      setPackageItems((itemsData || []) as MenuPackageItem[])

      // Fetch all menu items for categories using menu_item_categories
      const menuCategoryIds = (categoriesData || [])
        .map((cat: MenuPackageCategory & { menu_category?: { id: string } }) => 
          cat.menu_category?.id
        )
        .filter((id): id is string => !!id)

      let fetchedMenuItems: MenuItem[] = []
      const itemsMapByCategory: Record<string, MenuItem[]> = {}
      
      if (menuCategoryIds.length > 0) {
        // Pobierz wszystkie dania z kategorii używając menu_item_categories
        const allItemsPromises = menuCategoryIds.map(async (catId) => {
          const items = await getItemsForCategory(catId)
          itemsMapByCategory[catId] = items
          return items
        })
        const allItemsArrays = await Promise.all(allItemsPromises)
        
        // Połącz wszystkie dania i usuń duplikaty
        const itemsMap = new Map<string, MenuItem>()
        allItemsArrays.flat().forEach(item => {
          if (!itemsMap.has(item.id)) {
            itemsMap.set(item.id, item)
          }
        })
        
        fetchedMenuItems = Array.from(itemsMap.values())
        setAllMenuItems(fetchedMenuItems)
        setCategoryItemsMap(itemsMapByCategory)
      }

      // Load existing selections from event_menu_items
      const { data: eventItems } = await supabase
        .from('event_menu_items')
        .select('*')
        .eq('event_id', eventId)

      if (eventItems && categoriesData) {
        const initialSelections: Record<string, CategorySelection> = {}
        
        // Pobierz wszystkie kategorie jednocześnie
        const categoryDataPromises = (categoriesData as Array<MenuPackageCategory & { menu_category?: { id: string; name: string } }>)
          .map(async (cat) => {
            const categoryId = cat.menu_category?.id
            const categoryName = cat.menu_category?.name
            if (!categoryId || !categoryName) return null

            // Get all items in this category (from package)
            const itemsInPackageCategory = ((itemsData || []) as MenuPackageItem[]).filter(
              (item) => item.menu_package_category_id === cat.id
            )
            
            // Get all menu items with this category using menu_item_categories
            const allCategoryMenuItems = await getItemsForCategory(categoryId)

            return {
              cat,
              categoryId,
              categoryName,
              itemsInPackageCategory,
              allCategoryMenuItems,
            }
          })

        const categoryDataResults = await Promise.all(categoryDataPromises)

        categoryDataResults.forEach((catData) => {
          if (!catData) return

          // Find selected items that belong to this category
          const selected = (eventItems as Array<{ menu_item_id: string; quantity_percentage: number }>)
            .filter((ei) => {
              // Check if it's in package items for this category
              const inPackage = catData.itemsInPackageCategory.some((pi) => pi.menu_item_id === ei.menu_item_id)
              // Or if it's in all menu items for this category
              const inCategory = catData.allCategoryMenuItems.some((mi) => mi.id === ei.menu_item_id)
              return inPackage || inCategory
            })
            .map((ei) => ({
              menu_item_id: ei.menu_item_id,
              quantity_percentage: ei.quantity_percentage,
            }))

          if (selected.length > 0) {
            initialSelections[catData.cat.id] = {
              categoryId: catData.cat.id,
              selectedItems: selected,
            }
          }
        })
        
        setSelections(initialSelections)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd')
    } finally {
      setLoading(false)
    }
  }, [packageId, eventId, getItemsForCategory])

  const toggleItemSelection = useCallback(
    (categoryId: string, itemId: string, allowPercentage: boolean) => {
      setSelections((prev) => {
        const category = prev[categoryId] || { categoryId, selectedItems: [] }
        const existingIndex = category.selectedItems.findIndex(
          (item) => item.menu_item_id === itemId
        )

        if (existingIndex >= 0) {
          // Remove item
          const newItems = category.selectedItems.filter((item) => item.menu_item_id !== itemId)
          if (newItems.length === 0) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [categoryId]: _, ...rest } = prev
            return rest
          }
          return {
            ...prev,
            [categoryId]: {
              ...category,
              selectedItems: newItems,
            },
          }
        } else {
          // Add item with default 100% or based on remaining percentage
          const currentTotal = category.selectedItems.reduce(
            (sum, item) => sum + item.quantity_percentage,
            0
          )
          const defaultPercentage = allowPercentage && currentTotal < 100 ? 100 - currentTotal : 100

          return {
            ...prev,
            [categoryId]: {
              ...category,
              selectedItems: [
                ...category.selectedItems,
                {
                  menu_item_id: itemId,
                  quantity_percentage: defaultPercentage,
                },
              ],
            },
          }
        }
      })
    },
    []
  )

  const updateItemPercentage = useCallback(
    (categoryId: string, itemId: string, percentage: number) => {
      setSelections((prev) => {
        const category = prev[categoryId]
        if (!category) return prev

        return {
          ...prev,
          [categoryId]: {
            ...category,
            selectedItems: category.selectedItems.map((item) =>
              item.menu_item_id === itemId
                ? { ...item, quantity_percentage: Math.max(0, Math.min(100, percentage)) }
                : item
            ),
          },
        }
      })
    },
    []
  )

  const validateCategoryLimits = useCallback(
    (categoryId: string): { valid: boolean; message?: string } => {
      const category = packageCategories.find((cat) => cat.id === categoryId)
      if (!category) return { valid: true }

      const selection = selections[categoryId]
      const count = selection?.selectedItems.length || 0

      if (count < category.min_selections) {
        return {
          valid: false,
          message: `Musisz wybrać minimum ${category.min_selections} ${pluralizeDish(category.min_selections)} w tej kategorii`,
        }
      }

      if (count > category.max_selections) {
        return {
          valid: false,
          message: `Możesz wybrać maksimum ${category.max_selections} ${pluralizeDish(category.max_selections)} w tej kategorii`,
        }
      }

      return { valid: true }
    },
    [packageCategories, selections]
  )

  const validateAllCategories = useCallback((): {
    valid: boolean
    invalidCategories: Array<{ categoryId: string; message: string }>
  } => {
    const invalidCategories: Array<{ categoryId: string; message: string }> = []

    packageCategories.forEach((category) => {
      const validation = validateCategoryLimits(category.id)
      if (!validation.valid && validation.message) {
        invalidCategories.push({
          categoryId: category.id,
          message: validation.message,
        })
      }
    })

    return {
      valid: invalidCategories.length === 0,
      invalidCategories,
    }
  }, [packageCategories, validateCategoryLimits])

  const calculateTotalPrice = useCallback((): number => {
    let total = 0

    Object.values(selections).forEach((categorySelection) => {
      categorySelection.selectedItems.forEach((selectedItem) => {
        // First try to find in package items
        const packageItem = packageItems.find(
          (pi) =>
            pi.menu_item_id === selectedItem.menu_item_id &&
            pi.menu_package_category_id === categorySelection.categoryId
        )

        // Skip unlimited items in price calculation
        if (packageItem?.is_unlimited) {
          return // Skip this item, don't add to total
        }

        let price = 0
        if (packageItem) {
          // Use package item price (may override menu item price)
          price = packageItem.price_per_person || packageItem.menu_item?.price || 0
        } else {
          // Item is not in package, get price from menu_items
          const menuItem = allMenuItems.find((mi) => mi.id === selectedItem.menu_item_id)
          if (menuItem) {
            price = menuItem.price
          }
        }

        const quantity = Math.ceil((selectedItem.quantity_percentage / 100) * numberOfGuests)
        total += price * quantity
      })
    })

    return total
  }, [selections, packageItems, allMenuItems, numberOfGuests])

  const saveSelections = useCallback(async (): Promise<{ error: string | null }> => {
    try {
      // Get old menu items before deletion for logging
      const { data: oldMenuItems } = await supabase
        .from('event_menu_items')
        .select('*')
        .eq('event_id', eventId)

      // Delete existing event menu items
      await supabase.from('event_menu_items').delete().eq('event_id', eventId)

      // Insert new selections
      const itemsToInsert: Array<{
        event_id: string
        menu_item_id: string
        quantity_percentage: number
      }> = Object.values(selections).flatMap((categorySelection) =>
        categorySelection.selectedItems.map((item) => ({
          event_id: eventId,
          menu_item_id: item.menu_item_id,
          quantity_percentage: item.quantity_percentage,
        }))
      )

      if (itemsToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from('event_menu_items')
          .insert(itemsToInsert as unknown as never)

        if (insertError) throw insertError
      }

      // Update event with package
      const { error: updateError } = await supabase
        .from('events')
        .update({ menu_package_id: packageId } as unknown as never)
        .eq('id', eventId)

      if (updateError) throw updateError

      // Log the menu change
      await logMenuChange(
        eventId,
        user?.id || null,
        'UPDATE',
        oldMenuItems || [],
        itemsToInsert
      )

      return { error: null }
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd' }
    }
  }, [selections, eventId, packageId, user])

  const getAllItemsForCategory = useCallback((categoryId: string): {
    packageItems: MenuPackageItem[]
    otherItems: MenuItem[]
  } => {
    const category = packageCategories.find((cat) => cat.id === categoryId) as MenuPackageCategory & { menu_category?: { id: string; name: string } }
    if (!category) return { packageItems: [], otherItems: [] }

    const menuCategoryId = category.menu_category?.id
    if (!menuCategoryId) return { packageItems: [], otherItems: [] }

    // Get items from package
    const itemsFromPackage = packageItems.filter(
      (item) => item.menu_package_category_id === categoryId
    )

    // Get all menu items with this category using menu_item_categories (from state)
    const allCategoryItems = categoryItemsMap[menuCategoryId] || []

    // Get items that are NOT in the package
    const packageItemIds = new Set(itemsFromPackage.map((pi) => pi.menu_item_id))
    const otherItems = allCategoryItems.filter((item) => !packageItemIds.has(item.id))

    return {
      packageItems: itemsFromPackage,
      otherItems,
    }
  }, [packageCategories, packageItems, categoryItemsMap])

  return {
    packageCategories,
    packageItems,
    allMenuItems,
    selections,
    loading,
    error,
    fetchPackageData,
    toggleItemSelection,
    updateItemPercentage,
    validateCategoryLimits,
    validateAllCategories,
    calculateTotalPrice,
    saveSelections,
    getAllItemsForCategory,
  }
}
